import React, { useState, useEffect } from 'react';
import { auth, database, functions } from './firebase';
import { ref, onValue } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { useImpersonation } from './ImpersonationContext';

function NotificationsManagement() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [players, setPlayers] = useState([]);
  const [playerNumbers, setPlayerNumbers] = useState({}); // {playerId: mobileNumber}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Ad-hoc notification state
  const [notifyTarget, setNotifyTarget] = useState('all'); // 'all' or tournament ID
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showNotifySection, setShowNotifySection] = useState(true);
  
  // Impersonation context
  const { effectiveUserId } = useImpersonation();

  // Load tournaments on mount
  useEffect(() => {
    const loadTournaments = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      const userId = effectiveUserId || user.uid;
      const tournamentsRef = ref(database, `organisers/${userId}/tournaments`);
      onValue(tournamentsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const tournamentsList = Object.values(data).sort((a, b) => 
            new Date(b.starttime) - new Date(a.starttime)
          );
          setTournaments(tournamentsList);
        }
      });
    };

    loadTournaments();
  }, [effectiveUserId]);

  // Load players when tournament selected
  useEffect(() => {
    if (!selectedTournament) {
      setPlayers([]);
      return;
    }

    const loadPlayers = async () => {
      setLoading(true);
      try {
        const getPlayersWithPhones = httpsCallable(functions, 'getPlayersWithPhones');
        const result = await getPlayersWithPhones({ tournamentId: selectedTournament });
        const playersList = result.data.players;
        setPlayers(playersList);
        const numbers = {};
        for (const p of playersList) {
          if (p.mobileNumber) numbers[p.playerId] = p.mobileNumber;
        }
        setPlayerNumbers(numbers);
      } catch (error) {
        console.error('Error loading players:', error);
        setMessage({ type: 'error', text: 'Failed to load players' });
      } finally {
        setLoading(false);
      }
    };

    loadPlayers();
  }, [selectedTournament, tournaments]);

  const handleNumberChange = (playerId, value) => {
    // Format: allow only numbers, spaces, and + for international
    const formatted = value.replace(/[^\d\s+]/g, '');
    setPlayerNumbers(prev => ({
      ...prev,
      [playerId]: formatted
    }));
  };

  const savePlayerNumber = async (player) => {
    const number = playerNumbers[player.playerId] || '';
    const cleanNumber = number.replace(/\s/g, '');
    if (cleanNumber && cleanNumber.length < 10) {
      setMessage({ type: 'error', text: 'Phone number seems too short' });
      return;
    }

    setSaving(prev => ({ ...prev, [player.playerId]: true }));

    try {
      const setPlayerMobileNumber = httpsCallable(functions, 'setPlayerMobileNumber');
      await setPlayerMobileNumber({
        tournamentId: selectedTournament,
        playerId: player.playerId,
        mobileNumber: cleanNumber || null,
      });
      setMessage({ type: 'success', text: `Saved number for ${player.name}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving player number:', error);
      setMessage({ type: 'error', text: 'Failed to save number' });
    } finally {
      setSaving(prev => ({ ...prev, [player.playerId]: false }));
    }
  };

  const removePlayerNumber = async (player) => {
    if (!window.confirm(`Remove phone number for ${player.name}?`)) return;

    setSaving(prev => ({ ...prev, [player.playerId]: true }));

    try {
      const setPlayerMobileNumber = httpsCallable(functions, 'setPlayerMobileNumber');
      await setPlayerMobileNumber({
        tournamentId: selectedTournament,
        playerId: player.playerId,
        mobileNumber: null,
      });
      setPlayerNumbers(prev => {
        const updated = { ...prev };
        delete updated[player.playerId];
        return updated;
      });
      setMessage({ type: 'success', text: `Removed number for ${player.name}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error removing player number:', error);
      setMessage({ type: 'error', text: 'Failed to remove number' });
    } finally {
      setSaving(prev => ({ ...prev, [player.playerId]: false }));
    }
  };

  // Filter players by search term
  const filteredPlayers = players.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count players with numbers
  const playersWithNumbers = players.filter(p => playerNumbers[p.playerId]).length;

  // Send ad-hoc notification
  const handleSendNotification = async () => {
    if (!notifyTitle.trim() || !notifyMessage.trim()) {
      setMessage({ type: 'error', text: 'Please enter both a title and message' });
      return;
    }
    
    setSendingNotification(true);
    try {
      const sendNotification = httpsCallable(functions, 'sendAdHocNotification');
      const result = await sendNotification({
        title: notifyTitle.trim(),
        message: notifyMessage.trim(),
        targetType: notifyTarget === 'all' ? 'all' : 'tournament',
        tournamentId: notifyTarget === 'all' ? null : notifyTarget,
        effectiveUserId: effectiveUserId,
      });
      
      console.log('📢 Notification sent:', result.data);
      
      if (result.data.sent > 0) {
        setMessage({ type: 'success', text: `Notification sent to ${result.data.sent} player(s)` });
        setNotifyTitle('');
        setNotifyMessage('');
      } else {
        setMessage({ type: 'error', text: 'No premium players found to notify' });
      }
      
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      
    } catch (err) {
      console.error('❌ Failed to send notification:', err);
      setMessage({ type: 'error', text: 'Failed to send notification. Please try again.' });
    } finally {
      setSendingNotification(false);
    }
  };

  const styles = {
    card: {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    title: {
      fontSize: '20px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    select: {
      padding: '10px 16px',
      backgroundColor: '#374151',
      color: 'white',
      border: '1px solid #4b5563',
      borderRadius: '8px',
      fontSize: '14px',
      minWidth: '250px',
      cursor: 'pointer'
    },
    searchInput: {
      padding: '10px 16px',
      backgroundColor: '#374151',
      color: 'white',
      border: '1px solid #4b5563',
      borderRadius: '8px',
      fontSize: '14px',
      width: '200px'
    },
    stats: {
      display: 'flex',
      gap: '16px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    statBadge: {
      padding: '8px 16px',
      backgroundColor: '#374151',
      borderRadius: '8px',
      fontSize: '14px'
    },
    playerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      backgroundColor: '#374151',
      borderRadius: '8px',
      marginBottom: '8px'
    },
    playerName: {
      flex: '1',
      minWidth: '150px',
      fontWeight: '500'
    },
    phoneInput: {
      padding: '8px 12px',
      backgroundColor: '#1f2937',
      color: 'white',
      border: '1px solid #4b5563',
      borderRadius: '6px',
      fontSize: '14px',
      width: '150px'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      border: 'none'
    },
    saveButton: {
      backgroundColor: '#16a34a',
      color: 'white'
    },
    removeButton: {
      backgroundColor: '#dc2626',
      color: 'white'
    },
    disabledButton: {
      backgroundColor: '#4b5563',
      color: '#9ca3af',
      cursor: 'not-allowed'
    },
    message: {
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px'
    },
    successMessage: {
      backgroundColor: '#065f46',
      color: '#6ee7b7'
    },
    errorMessage: {
      backgroundColor: '#7f1d1d',
      color: '#fca5a5'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#9ca3af'
    },
    hasNumber: {
      color: '#10b981'
    },
    noNumber: {
      color: '#6b7280'
    },
    controls: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: '16px'
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          📢 Player Notifications
        </h2>
      </div>

      {/* Ad-hoc Notification Section */}
      <div style={{ marginBottom: '24px' }}>
        <div 
          onClick={() => setShowNotifySection(!showNotifySection)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '12px 16px',
            backgroundColor: '#374151',
            borderRadius: showNotifySection ? '8px 8px 0 0' : '8px',
            marginBottom: showNotifySection ? '0' : '0'
          }}
        >
          <span style={{ flex: 1, fontWeight: '600' }}>📤 Send Notification to Players</span>
          <span style={{ color: '#9ca3af' }}>{showNotifySection ? '▼' : '▶'}</span>
        </div>
        
        {showNotifySection && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#374151', 
            borderRadius: '0 0 8px 8px',
            borderTop: '1px solid #4b5563'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#9ca3af', marginBottom: '6px' }}>
                Send to:
              </label>
              <select
                value={notifyTarget}
                onChange={(e) => setNotifyTarget(e.target.value)}
                style={{
                  ...styles.select,
                  width: '100%',
                  minWidth: 'auto'
                }}
              >
                <option value="all">All Players (across all tournaments)</option>
                {tournaments.map(t => (
                  <option key={t.tournamentId} value={t.tournamentId.toString()}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#9ca3af', marginBottom: '6px' }}>
                Title
              </label>
              <input
                type="text"
                value={notifyTitle}
                onChange={(e) => setNotifyTitle(e.target.value)}
                placeholder="e.g., Tournament Update"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#1f2937',
                  color: 'white',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#9ca3af', marginBottom: '6px' }}>
                Message
              </label>
              <textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Enter your message..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#1f2937',
                  color: 'white',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            
            <button
              onClick={handleSendNotification}
              disabled={sendingNotification || !notifyTitle.trim() || !notifyMessage.trim()}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: sendingNotification ? '#4b5563' : '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: sendingNotification ? 'not-allowed' : 'pointer'
              }}
            >
              {sendingNotification ? '📤 Sending...' : '📢 Send Push Notification'}
            </button>
            
            <p style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
              Only premium players with the mobile app will receive notifications.
            </p>
          </div>
        )}
      </div>

      {message.text && (
        <div style={{
          ...styles.message,
          ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
        }}>
          {message.type === 'success' ? '✅' : '⚠️'} {message.text}
        </div>
      )}

      {/* Phone Number Management Section */}
      <div style={{ 
        padding: '12px 16px', 
        backgroundColor: '#374151', 
        borderRadius: '8px 8px 0 0',
        marginTop: '8px'
      }}>
        <span style={{ fontWeight: '600' }}>📱 Manage Player Phone Numbers</span>
      </div>
      
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#374151', 
        borderRadius: '0 0 8px 8px',
        borderTop: '1px solid #4b5563'
      }}>
        <div style={styles.controls}>
          <select
            value={selectedTournament}
            onChange={(e) => setSelectedTournament(e.target.value)}
            style={styles.select}
          >
            <option value="">Select a tournament...</option>
            {tournaments.map(tournament => (
              <option key={tournament.tournamentId} value={tournament.tournamentId.toString()}>
                {tournament.name}
              </option>
            ))}
          </select>

          {selectedTournament && players.length > 0 && (
            <input
              type="text"
              placeholder="🔍 Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          )}
        </div>

      {selectedTournament && players.length > 0 && (
        <div style={styles.stats}>
          <span style={styles.statBadge}>
            👥 {players.length} players
          </span>
          <span style={{...styles.statBadge, color: '#10b981'}}>
            📱 {playersWithNumbers} with numbers
          </span>
          <span style={{...styles.statBadge, color: '#f59e0b'}}>
            ❓ {players.length - playersWithNumbers} missing
          </span>
        </div>
      )}

      {!selectedTournament ? (
        <div style={styles.emptyState}>
          Select a tournament to manage player phone numbers
        </div>
      ) : loading ? (
        <div style={styles.emptyState}>
          Loading players...
        </div>
      ) : players.length === 0 ? (
        <div style={styles.emptyState}>
          No players found in this tournament
        </div>
      ) : filteredPlayers.length === 0 ? (
        <div style={styles.emptyState}>
          No players match your search
        </div>
      ) : (
        <div>
          {filteredPlayers.map(player => (
            <div key={player.playerId} style={styles.playerRow}>
              <span style={{
                ...styles.playerName,
                ...(playerNumbers[player.playerId] ? styles.hasNumber : styles.noNumber)
              }}>
                {playerNumbers[player.playerId] ? '✅' : '❌'} {player.name}
              </span>
              
              <input
                type="tel"
                placeholder="07xxx xxxxxx"
                value={playerNumbers[player.playerId] || ''}
                onChange={(e) => handleNumberChange(player.playerId, e.target.value)}
                style={styles.phoneInput}
              />
              
              <button
                onClick={() => savePlayerNumber(player)}
                disabled={saving[player.playerId]}
                style={{
                  ...styles.button,
                  ...(saving[player.playerId] ? styles.disabledButton : styles.saveButton)
                }}
              >
                {saving[player.playerId] ? '...' : '💾 Save'}
              </button>

              {playerNumbers[player.playerId] && (
                <button
                  onClick={() => removePlayerNumber(player)}
                  disabled={saving[player.playerId]}
                  style={{
                    ...styles.button,
                    ...(saving[player.playerId] ? styles.disabledButton : styles.removeButton)
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#2d3748', borderRadius: '8px', fontSize: '14px', color: '#9ca3af' }}>
        <strong>💡 Tip:</strong> Phone numbers are linked to CueScore player IDs. Once you add a number, 
        it will automatically appear in future tournaments for the same player.
      </div>
      </div>
    </div>
  );
}

export default NotificationsManagement;